import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent
} from '@mui/material';
import { 
  BarChart, 
  Bar, 
  PieChart,
  Pie,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { fetchMapInteractionStatistics } from '../../api/userInteractionReports';

// Colors for charts
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#8DD1E1'];

/**
 * Component for displaying map interaction statistics
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter values
 */
function MapInteractionsReport({ filters }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);

  // Load map interaction statistics when filters change
  useEffect(() => {
    const loadStatistics = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchMapInteractionStatistics(filters);
        setStatistics(data);
      } catch (error) {
        console.error('Error loading map interaction statistics:', error);
        setError('Failed to load map interaction statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadStatistics();
  }, [filters]);

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // If no data, show message
  if (!statistics || statistics.totalMapInteractions === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        No map interaction data available for the selected filters.
      </Alert>
    );
  }

  // Prepare data for charts
  const interactionTypeData = Object.entries(statistics.mapInteractionsByType || {})
    .map(([type, count]) => ({ name: type, value: count }));
  
  const layerData = Object.entries(statistics.mapInteractionsByLayer || {})
    .map(([layer, count]) => ({ name: layer, value: count }));

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h6" gutterBottom>
        Map Interaction Statistics
      </Typography>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {/* Total Map Interactions Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Map Interactions
            </Typography>
            <Typography variant="h4">
              {statistics.totalMapInteractions}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Common Interaction Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Common Interaction
            </Typography>
            <Typography variant="h5">
              {interactionTypeData.length > 0 
                ? interactionTypeData.sort((a, b) => b.value - a.value)[0].name 
                : 'N/A'}
            </Typography>
            <Typography variant="body2">
              {interactionTypeData.length > 0 
                ? `${interactionTypeData.sort((a, b) => b.value - a.value)[0].value} interactions` 
                : '0 interactions'}
            </Typography>
          </CardContent>
        </Card>
        
        {/* Most Active Layer Card */}
        <Card sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Most Active Layer
            </Typography>
            <Typography variant="h5">
              {statistics.mostActiveMapLayers?.[0]?.layer || 'N/A'}
            </Typography>
            <Typography variant="body2">
              {statistics.mostActiveMapLayers?.[0]?.count || 0} interactions
            </Typography>
          </CardContent>
        </Card>
      </Box>
      
      {/* Map Interactions by Day Chart */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Map Interactions by Day
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={statistics.mapInteractionsByDayArray || []}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#8884d8" name="Map Interactions" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>
      
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        {/* Interaction Types Pie Chart */}
        <Paper sx={{ p: 2, flex: '1 1 300px' }}>
          <Typography variant="subtitle1" gutterBottom>
            Interaction Types
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={interactionTypeData}
                cx="50%"
                cy="50%"
                labelLine={true}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {interactionTypeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value, name) => [value, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
        
        {/* Map Layers Pie Chart */}
        {layerData.length > 0 && (
          <Paper sx={{ p: 2, flex: '1 1 300px' }}>
            <Typography variant="subtitle1" gutterBottom>
              Map Layers
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={layerData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {layerData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [value, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        )}
      </Box>
      
      {/* Most Active Map Layers Table */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Most Active Map Layers
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="most active map layers table">
            <TableHead>
              <TableRow>
                <TableCell>Layer</TableCell>
                <TableCell align="right">Interactions</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(statistics.mostActiveMapLayers || []).map((layer) => (
                <TableRow
                  key={layer.layer}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {layer.layer}
                  </TableCell>
                  <TableCell align="right">{layer.count}</TableCell>
                  <TableCell align="right">
                    {((layer.count / statistics.totalMapInteractions) * 100).toFixed(2)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Interaction Types Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Interaction Types
        </Typography>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="interaction types table">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Percentage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {interactionTypeData
                .sort((a, b) => b.value - a.value)
                .map((type) => (
                  <TableRow
                    key={type.name}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">
                      {type.name}
                    </TableCell>
                    <TableCell align="right">{type.value}</TableCell>
                    <TableCell align="right">
                      {((type.value / statistics.totalMapInteractions) * 100).toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default MapInteractionsReport;
